import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { friendlyAuthError } from "@/lib/auth-errors";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>): { next?: string; email?: string } => {
    // Accept either `next` (legacy) or `redirect` (preferred) — they mean the same thing.
    const raw = typeof s.next === "string" ? s.next : (typeof s.redirect === "string" ? s.redirect : undefined);
    // Tolerate legacy links where the path arrived percent-encoded ("%2Fmanager").
    let next = raw;
    if (raw && !raw.startsWith("/") && /^%2[fF]/.test(raw)) {
      try { next = decodeURIComponent(raw); } catch { next = raw; }
    }
    return {
      next,
      // Optional `email` lets us prefill from "account already exists" CTAs.
      email: typeof s.email === "string" ? s.email : undefined,
    };
  },
  head: () => ({
    meta: [
      { title: "Sign in — MissionReady" },
      { name: "description", content: "Sign in to your MissionReady workspace to manage patrols, dispatch, incidents, and client reporting." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Sign in — MissionReady" },
      { property: "og:description", content: "Sign in to your MissionReady workspace." },
      { property: "og:url", content: "https://missionready.dev/login" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/login" }],
  }),
});

const REMEMBER_KEY = "mr.rememberLogin";
const EMAIL_KEY = "mr.rememberedEmail";

function LoginPage() {
  const navigate = useNavigate();
  const { next, email: emailFromQuery } = Route.useSearch();
  const { session, memberships, loading, membershipsLoading } = useAuth();
  const [email, setEmail] = useState(emailFromQuery ?? "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Until React has taken over the form, a tap would submit it as a plain page
  // load (sending the password into the URL and signing nobody in).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { setHydrated(true); }, []);


  useEffect(() => {
    if (typeof window === "undefined") return;
    const flag = localStorage.getItem(REMEMBER_KEY);
    const savedEmail = localStorage.getItem(EMAIL_KEY) ?? "";
    if (flag === "0") {
      setRemember(false);
    } else {
      setRemember(true);
      // Query-string email wins (it's the user's most recent intent).
      if (!emailFromQuery && savedEmail) setEmail(savedEmail);
    }
  }, [emailFromQuery]);

  useEffect(() => {
    if (loading || membershipsLoading) return;
    if (session) {
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      const dest = safeNext ?? (memberships.length ? "/operations" : "/onboarding");
      navigate({ to: dest, replace: true });
    }
  }, [session, memberships.length, loading, membershipsLoading, navigate, next]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) return toast.error(friendlyAuthError(error));

    if (typeof window !== "undefined") {
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, "1");
        localStorage.setItem(EMAIL_KEY, email);
      } else {
        localStorage.setItem(REMEMBER_KEY, "0");
        localStorage.removeItem(EMAIL_KEY);
      }
    }

    toast.success("Signed in");
  };

  return (
    // Extra bottom padding keeps the Sign in button reachable above the cookie
    // notice on small phone screens (it used to sit underneath it).
    <main className="flex min-h-dvh items-center justify-center bg-hero px-4 pb-64 pt-10">

      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-card">
          <h1 className="font-display text-2xl font-semibold">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your operations center.</p>

          {/* Primary CTA: Google — fastest sign-in for most operators. */}
          <div className="mt-6">
            <GoogleSignInButton
              label="Continue with Google"
              redirectTo={next && next.startsWith("/") && !next.startsWith("//") ? next : undefined}
            />
          </div>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or sign in with email <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" autoComplete="on">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <label htmlFor="remember" className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  id="remember"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(v === true)}
                />
                Remember me on this device
              </label>
              <Link to="/forgot-password" className="text-sm font-medium text-primary underline underline-offset-2">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" variant="outline" className="w-full" disabled={submitting || !hydrated}>
              {submitting ? "Signing in…" : "Sign in with email"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New here? <Link to="/signup" className="text-primary underline underline-offset-2">Start a free trial</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
